import Joi from '@hapi/joi';
import React from 'react';
import { CraConditionScores } from '../../../../../common/src/enums/CraConditionScores';
import { ITranslateProps, translate } from '../../../mixins/translate';
import { fetchGet } from '../../../store/fetch';
import { EditPanelButtons, EditPanelCell, EditPanelRow, PanelBody } from '../../elements';
import { ComponentSetStatePromised } from '../../utils/ComponentSetStatePromised';
import { ValidatingDropdownToggleCRA, ValidatingEditPanelSaveButton, ValidatingForm, ValidatingFormTextArea } from '../../validatingElements';

const schema = Joi.object().keys({
    craInspectionScore: Joi.number().allow(null, ''),
    craInspectionRemarks: Joi.string().when('craInspectionScore', { is: Joi.number(), then: Joi.required(), otherwise: Joi.allow(null, '') }),
});

interface ISurveyEditCraConditionVerificationProps extends ITranslateProps {
    surveyId: string;
    objectId: string;
    elementId?: undefined | string;
    unitId?: undefined | string;
    manifestationId?: undefined | string;
    selectedName?: undefined | string;
    selectedFailureModeUpdate: (selectedFailureModeId: string) => void;
    onSaved(): void;
}

interface ISurveyEditCraConditionVerificationState {
    conditionId?: undefined | string;
    isLoaded: boolean;
}

class SurveyEditCraConditionVerificationComponent extends ComponentSetStatePromised<
    ISurveyEditCraConditionVerificationProps,
    ISurveyEditCraConditionVerificationState
> {
    constructor(props: ISurveyEditCraConditionVerificationProps) {
        super(props);
        this.state = { isLoaded: false };
    }

    public override async componentDidMount() {
        await this.load();
    }

    public override async componentDidUpdate(prevProps: ISurveyEditCraConditionVerificationProps) {
        const shouldReload =
            prevProps.elementId !== this.props.elementId ||
            prevProps.unitId !== this.props.unitId ||
            prevProps.manifestationId !== this.props.manifestationId;

        if (shouldReload) {
            await this.setStatePromised({ isLoaded: false });
            await this.load();
        }
    }

    public override render() {
        if (!this.state.isLoaded) {
            return null;
        }

        return (
            <PanelBody isForm={true}>
                <ValidatingForm
                    schema={schema}
                    createUrl={`/api/surveys/${this.props.surveyId}/units/${this.props.unitId}/condition`}
                    itemUrl={`/api/surveys/${this.props.surveyId}/units/${this.props.unitId}/condition/`}
                    updateUrl={`/api/surveys/${this.props.surveyId}/units/${this.props.unitId}/condition/${this.state.conditionId}`}
                    id={this.state.conditionId}
                    onSubmitted={() => this.props.onSaved()}
                >
                    <EditPanelRow>
                        <EditPanelCell rowCount={1}> {this.getRiskScoreDropDown('craInspectionScore')} </EditPanelCell>
                        <EditPanelCell rowCount={11}>
                            <ValidatingFormTextArea size="xs" name="craInspectionRemarks" placeholder={this.props.t('common.remarks')} />
                        </EditPanelCell>
                    </EditPanelRow>
                    <EditPanelButtons>
                        <ValidatingEditPanelSaveButton small={true} />
                    </EditPanelButtons>
                </ValidatingForm>
            </PanelBody>
        );
    }

    private getRiskScoreDropDown(name: string) {
        return (
            <ValidatingDropdownToggleCRA
                name={name}
                header={this.props.t('failureMode.riskScoreHeader')}
                caretVisible={false}
                options={[
                    { text: this.props.t(`cra.riskScore${CraConditionScores.LowRisk}`), value: CraConditionScores.LowRisk },
                    { text: this.props.t(`cra.riskScore${CraConditionScores.BelowAverageRisk}`), value: CraConditionScores.BelowAverageRisk },
                    { text: this.props.t(`cra.riskScore${CraConditionScores.AboveAverageRisk}`), value: CraConditionScores.AboveAverageRisk },
                    { text: this.props.t(`cra.riskScore${CraConditionScores.HighRisk}`), value: CraConditionScores.HighRisk },
                ]}
                directSave={false}
            />
        );
    }

    private async load() {
        const url = `/api/surveys/${this.props.surveyId}/units/${this.props.unitId}/condition`;

        const result = await fetchGet(url);
        await this.setStatePromised({ conditionId: result.json.id, isLoaded: true });
    }
}

// eslint-disable-next-line @typescript-eslint/naming-convention
export const SurveyEditCraConditionVerification = translate()(SurveyEditCraConditionVerificationComponent);
